import React from 'react'
import { toast } from 'react-toastify'
import {
  GoogleReCaptchaProvider,
  useGoogleReCaptcha,
} from 'react-google-recaptcha-v3'
import { ProposalWizard } from 'components'
import {
  validateCpf,
  validateCnpj,
  validateEmail,
  validateBirthDate,
  validateFullName,
  validateMandatoryPhone,
} from 'utils/validations'
import debounce from 'lib/debounce'
import fileToBase64 from 'utils/fileToBase64'
import { getUtms } from 'utils/Utms'
import { sendProposalWizard, getSalesChannel } from 'service/proposalWizard'
import {
  getProposalApiAmounts,
  isProposalValueBelowNegotiationRange,
} from 'store/property/utils/general'

const PAYMENT_METHOD_API = {
  vista: 'a_vista',
  parcelamento: 'parcelamento',
  financiamento: 'financiamento',
}

// O CurrencyInput (locale pt-BR) devolve o valor com vírgula decimal, ex.: "150000,5"
const parseCurrency = value => Number(String(value ?? '').replace(',', '.'))

const initialForm = {
  proposalValue: '',
  justification: '',
  attachments: [],
  hasPartner: '',
  partnerCode: '',
  salesChannelName: '',
  salesChannelType: '',
  paymentMethod: '',
  downPaymentValue: '',
  installmentsCount: '',
  personType: 'pf',
  name: '',
  document: '',
  birthDate: '',
  email: '',
  whatsapp: '',
  representativeName: '',
  representativeDocument: '',
  representativeBirthDate: '',
  observations: '',
}

const buildErrors = (form, isBelowNegotiationRange) => {
  const isProposalValueFilled = parseCurrency(form.proposalValue) > 0
  const isProposalValueBelowMin =
    isProposalValueFilled && isBelowNegotiationRange
  const isProposalValueValid = isProposalValueFilled && !isProposalValueBelowMin
  const isJustificationValid = (form.justification?.trim().length || 0) >= 30
  const isPartnerStepValid =
    form.hasPartner === 'nao' ||
    (form.hasPartner === 'sim' && Boolean(form.salesChannelName?.trim()))
  const requiresDownPayment =
    form.paymentMethod === 'financiamento' ||
    form.paymentMethod === 'parcelamento'
  const requiresInstallments = form.paymentMethod === 'parcelamento'
  const isDownPaymentValid =
    !requiresDownPayment || parseCurrency(form.downPaymentValue) > 0
  const isInstallmentsValid =
    !requiresInstallments ||
    (Number(form.installmentsCount) > 0 &&
      Number(form.installmentsCount) <= 420)
  const isPaymentValid =
    Boolean(form.paymentMethod) && isDownPaymentValid && isInstallmentsValid
  const isNameValid = validateFullName(form.name || '')
  const isDocumentValid =
    form.personType === 'pj'
      ? validateCnpj(form.document)
      : validateCpf(form.document)
  const isEmailValid = validateEmail(form.email || '')
  const isWhatsappValid = validateMandatoryPhone(form.whatsapp || '')
  const isPj = form.personType === 'pj'
  const isBirthDateValid = isPj || validateBirthDate(form.birthDate || '')
  const isRepresentativeNameValid =
    !isPj || validateFullName(form.representativeName || '')
  const isRepresentativeDocumentValid =
    !isPj || validateCpf(form.representativeDocument)
  const isRepresentativeBirthDateValid =
    !isPj || validateBirthDate(form.representativeBirthDate || '')

  return {
    isProposalValueValid,
    isProposalValueBelowMin,
    isJustificationValid,
    isPartnerStepValid,
    isDownPaymentValid,
    isInstallmentsValid,
    isPaymentValid,
    isNameValid,
    isDocumentValid,
    isEmailValid,
    isWhatsappValid,
    isBirthDateValid,
    isRepresentativeNameValid,
    isRepresentativeDocumentValid,
    isRepresentativeBirthDateValid,
    isFormValid:
      isProposalValueValid &&
      isJustificationValid &&
      isPartnerStepValid &&
      isPaymentValid &&
      isNameValid &&
      isDocumentValid &&
      isEmailValid &&
      isWhatsappValid &&
      isBirthDateValid &&
      isRepresentativeNameValid &&
      isRepresentativeDocumentValid &&
      isRepresentativeBirthDateValid,
  }
}

const ProposalWizardContainerInner = ({
  opened = false,
  closeModal,
  property,
  initialScreen = 'intro',
  initialFormValues = null,
}) => {
  const [screen, setScreen] = React.useState(initialScreen)
  const [faqOpenIndex, setFaqOpenIndex] = React.useState(null)
  const [form, setForm] = React.useState({
    ...initialForm,
    ...initialFormValues,
  })
  const [touched, setTouched] = React.useState({})
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [termsAccepted, setTermsAccepted] = React.useState(false)
  const [marketingAccepted, setMarketingAccepted] = React.useState(false)
  const [loadingSalesChannel, setLoadingSalesChannel] = React.useState(false)

  const { executeRecaptcha } = useGoogleReCaptcha()

  const handleReCaptchaVerify = React.useCallback(async () => {
    if (!executeRecaptcha) {
      return null
    }
    const token = await executeRecaptcha('submit')
    return token
  }, [executeRecaptcha])

  // A trava compara o valor LIQUIDO da proposta com o valor LIQUIDO do imovel
  // (valor_sem_comissao), e nao com o valor de venda que ja inclui honorarios.
  const isBelowNegotiationRange = isProposalValueBelowNegotiationRange(
    property?.makeProposal,
    property?.mainValueWithoutComission,
    parseCurrency(form.proposalValue),
    form.salesChannelType
  )

  const errors = buildErrors(form, isBelowNegotiationRange)

  React.useEffect(() => {
    if (opened) {
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.body.style.overflow = 'scroll'
    }
  }, [opened])

  const fetchSalesChannel = debounce(async code => {
    try {
      const res = await getSalesChannel(code)
      const data = res?.data ?? []
      const channel = Array.isArray(data) ? data[0] : null
      setForm(prev => ({
        ...prev,
        salesChannelName: channel?.nome || '',
        salesChannelType: channel?.tipo_canal || '',
      }))
    } catch (error) {
      toast.error(error.message)
    } finally {
      setLoadingSalesChannel(false)
    }
  }, 1000)

  const resetWizard = () => {
    setScreen('intro')
    setFaqOpenIndex(null)
    setForm(initialForm)
    setTouched({})
    setTermsAccepted(false)
    setMarketingAccepted(false)
  }

  const handleClose = () => {
    resetWizard()
    closeModal()
  }

  const handleChange = (field, value) => {
    if (field === 'hasPartner' && value !== 'sim') {
      setTouched(prev => ({ ...prev, partnerCode: false }))
    }

    if (field === 'paymentMethod' && value !== 'parcelamento') {
      setTouched(prev => ({ ...prev, installmentsCount: false }))
    }
    if (field === 'paymentMethod' && value === 'vista') {
      setTouched(prev => ({ ...prev, downPaymentValue: false }))
    }

    if (field === 'partnerCode') {
      if (value) {
        setLoadingSalesChannel(true)
        fetchSalesChannel(value)
      } else {
        setLoadingSalesChannel(false)
      }
    }

    setForm(prev => {
      if (field === 'hasPartner' && value !== 'sim') {
        return {
          ...prev,
          hasPartner: value,
          partnerCode: '',
          salesChannelName: '',
          salesChannelType: '',
        }
      }

      if (field === 'partnerCode') {
        return {
          ...prev,
          partnerCode: value,
          salesChannelName: '',
          salesChannelType: '',
        }
      }

      if (field === 'paymentMethod' && value === 'vista') {
        return {
          ...prev,
          paymentMethod: value,
          downPaymentValue: '',
          installmentsCount: '',
        }
      }

      if (field === 'paymentMethod' && value === 'financiamento') {
        return { ...prev, paymentMethod: value, installmentsCount: '' }
      }

      return { ...prev, [field]: value }
    })
  }

  const handleBlur = field => () =>
    setTouched(prev => ({ ...prev, [field]: true }))

  const handleAddAttachment = files => {
    setForm(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...files],
    }))
  }

  const handleRemoveAttachment = index => {
    setForm(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index),
    }))
  }

  const handleTryReview = () => {
    const showProposalData = errors.isPartnerStepValid
    const showPaymentMethod =
      showProposalData &&
      errors.isProposalValueValid &&
      errors.isJustificationValid
    const showProponent = showPaymentMethod && errors.isPaymentValid

    setTouched(prev => ({
      ...prev,
      hasPartner: true,
      ...(form.hasPartner === 'sim' && { partnerCode: true }),
      ...(showProposalData && {
        proposalValue: true,
        justification: true,
      }),
      ...(showPaymentMethod && {
        paymentMethod: true,
        downPaymentValue: true,
        installmentsCount: true,
      }),
      ...(showProponent && {
        name: true,
        document: true,
        birthDate: true,
        email: true,
        whatsapp: true,
        representativeName: true,
        representativeDocument: true,
        representativeBirthDate: true,
      }),
    }))

    if (errors.isFormValid) {
      setScreen('review')
    }
  }

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true)

      const {
        valorLiquidoProposta,
        valorTotalProposta,
      } = getProposalApiAmounts(
        property?.makeProposal,
        parseCurrency(form.proposalValue),
        form.salesChannelType
      )

      const anexos = await Promise.all(
        form.attachments.map(async file => ({
          nome: file.name,
          conteudo_base64: await fileToBase64(file),
        }))
      )

      const isPj = form.personType === 'pj'
      const utms = getUtms() || {}
      const tokenCaptcha = await handleReCaptchaVerify()

      const payload = {
        origem: 'emgea',
        recaptcha_token: tokenCaptcha,
        imovel_id: property?.propertyId,
        codigo_canal_venda: form.hasPartner === 'sim' ? form.partnerCode : null,
        valor_liquido_proposta: valorLiquidoProposta,
        valor_total_proposta: valorTotalProposta,
        justificativa: form.justification,
        forma_pagamento: PAYMENT_METHOD_API[form.paymentMethod],
        ...(form.paymentMethod !== 'vista' && {
          valor_entrada: parseCurrency(form.downPaymentValue),
        }),
        ...(form.paymentMethod === 'parcelamento' && {
          numero_parcelas: Number(form.installmentsCount),
        }),
        observacoes: form.observations || undefined,
        anexos: anexos.length ? anexos : undefined,
        tipo_pessoa: isPj ? 'juridica' : 'fisica',
        ...(isPj
          ? {
              razao_social: form.name,
              cnpj: form.document,
              nome_representante: form.representativeName,
              cpf_representante: form.representativeDocument,
              data_nascimento_representante: form.representativeBirthDate,
            }
          : {
              nome: form.name,
              cpf: form.document,
              data_nascimento: form.birthDate,
            }),
        whatsapp: form.whatsapp,
        email: form.email,
        send_mail_resale: marketingAccepted,
        send_mail_pgi: marketingAccepted,
        utm_source: utms.utm_source,
        utm_medium: utms.utm_medium,
        utm_campaign: utms.utm_campaign,
        utm_content: utms.utm_content,
        utm_term: utms.utm_term,
      }

      await sendProposalWizard(payload)
      setScreen('success')
    } catch (error) {
      toast.error(
        error.message || 'Erro ao registrar a proposta. Tente novamente.'
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <ProposalWizard
      opened={opened}
      closeModal={handleClose}
      screen={screen}
      property={property}
      faqOpenIndex={faqOpenIndex}
      onToggleFaq={index =>
        setFaqOpenIndex(prev => (prev === index ? null : index))
      }
      onCloseIntro={handleClose}
      onGoToForm={() => setScreen('form')}
      onGoToIntro={() => setScreen('intro')}
      form={form}
      touched={touched}
      errors={errors}
      onChange={handleChange}
      onBlur={handleBlur}
      onAddAttachment={handleAddAttachment}
      onRemoveAttachment={handleRemoveAttachment}
      onTryReview={handleTryReview}
      onGoToEdit={() => setScreen('form')}
      onSubmit={handleSubmit}
      onFinish={handleClose}
      isSubmitting={isSubmitting}
      termsAccepted={termsAccepted}
      onToggleTerms={() => setTermsAccepted(prev => !prev)}
      marketingAccepted={marketingAccepted}
      onToggleMarketing={() => setMarketingAccepted(prev => !prev)}
      loadingSalesChannel={loadingSalesChannel}
    />
  )
}

const ProposalWizardContainer = props => (
  <GoogleReCaptchaProvider
    reCaptchaKey="6LdRkDEiAAAAAKpMzmMN42ddh5XXIuSX_gkAOk2y"
    scriptProps={{ async: true }}
  >
    <ProposalWizardContainerInner {...props} />
  </GoogleReCaptchaProvider>
)

export default ProposalWizardContainer
